// ============================================================
// TMR Portal Admin — campos de formulário genéricos
// ============================================================
import type { HTMLInputTypeAttribute, ReactNode } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';
import { Hint } from './Tooltip';

type FieldError = string | null | false;
type InputMode = 'text' | 'decimal' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'none';

/**
 * A field's label, with the dashed underline and tooltip when the field carries a hint.
 *
 * The affordance is the underline itself rather than an "i" icon — the same choice {@link Hint} was
 * written for. Putting it here instead of at each call site is what keeps a hinted label identical
 * everywhere: a form that rolled its own would drift in offset, colour and hover behaviour.
 */
function FieldLabel({ label, hint, required }: { label: string; hint?: ReactNode; required?: boolean }) {
  return (
    <label className="pa-form-label">
      {hint ? <Hint tip={hint}>{label}</Hint> : label}
      {required && <span style={{ color: 'var(--danger)' }}> *</span>}
    </label>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  required,
  maxLength,
  suffix,
  mono,
  disabled,
  inputMode,
  type = 'text',
}: {
  label?: string;
  /** Explains the field in a tooltip, revealed by the label's dashed underline. */
  hint?: ReactNode;
  value: string;
  onChange?: (v: string) => void;
  onBlur?: () => void;
  error?: FieldError;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  suffix?: string;
  mono?: boolean;
  disabled?: boolean;
  inputMode?: InputMode;
  type?: HTMLInputTypeAttribute;
}) {
  return (
    <div>
      {label && <FieldLabel label={label} hint={hint} required={required} />}
      <div style={{ position: 'relative' }}>
        <input
          type={type}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode={inputMode}
          className="pa-input"
          style={{
            width: '100%',
            padding: suffix ? '9px 54px 9px 12px' : '9px 12px',
            fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
            fontSize: 14,
            border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-sm)',
            background: disabled ? 'var(--cream-100)' : 'var(--cream-50)',
            color: 'var(--ink-900)',
            outline: 'none',
          }}
        />
        {suffix && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, color: 'var(--fg-subtle)' }}>{suffix}</span>
        )}
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  value,
  onChange,
  options,
  error,
  required,
  placeholder,
  disabled,
}: {
  label?: string;
  /** Explains the field in a tooltip, revealed by the label's dashed underline. */
  hint?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  error?: FieldError;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      {label && <FieldLabel label={label} hint={hint} required={required} />}
      <div style={{ position: 'relative' }}>
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="pa-input"
          style={{
            width: '100%',
            padding: '9px 34px 9px 12px',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            appearance: 'none',
            border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-sm)',
            background: disabled ? 'var(--cream-100)' : 'var(--cream-50)',
            color: value ? 'var(--ink-900)' : 'var(--stone-400)',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <option value="">{placeholder || 'Selecione…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone-400)', pointerEvents: 'none', display: 'flex' }}>
          <StrokeIcon d={Icons.chevronD} size={15} />
        </span>
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  );
}

export function ChoiceGrid({
  value,
  onChange,
  options,
  columns = 2,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  columns?: number;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 8 }}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{
              padding: '11px 14px',
              textAlign: 'left',
              cursor: 'pointer',
              border: `1px solid ${active ? 'var(--forest-900)' : 'var(--border-default)'}`,
              background: active ? 'var(--sage-100)' : 'var(--cream-50)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13.5,
              fontWeight: active ? 600 : 450,
              color: 'var(--ink-900)',
              boxShadow: active ? 'inset 0 0 0 1px var(--forest-900)' : 'none',
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

// Trilho de passos (rail lateral do wizard)
export function StepRail({
  steps,
  step,
  maxStep,
  onGo,
}: {
  steps: { label: string }[];
  step: number;
  maxStep: number;
  onGo: (i: number) => void;
}) {
  return (
    <div
      className="pa-step-rail"
      style={{ width: 216, flexShrink: 0, borderRight: '1px solid var(--border-default)', padding: '20px 12px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}
    >
      {steps.map((s, i) => {
        const active = i === step;
        const reachable = i <= maxStep;
        const done = i < step;
        return (
          <button
            key={s.label}
            type="button"
            onClick={() => reachable && onGo(i)}
            disabled={!reachable}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 10px',
              border: 0,
              borderRadius: 'var(--radius-sm)',
              background: active ? 'var(--sage-100)' : 'transparent',
              cursor: reachable ? 'pointer' : 'default',
              textAlign: 'left',
              opacity: reachable ? 1 : 0.45,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
                background: active ? 'var(--forest-900)' : done ? 'var(--forest-700)' : 'var(--stone-200)',
                color: active || done ? 'var(--cream-50)' : 'var(--fg-subtle)',
              }}
            >
              {done ? <StrokeIcon d={Icons.check} size={12} strokeWidth={2.4} /> : i + 1}
            </span>
            <span style={{ fontSize: 13.5, fontWeight: active ? 600 : 450, color: active ? 'var(--ink-900)' : 'var(--fg-muted)' }}>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}
